import type { Circuit, CircuitSurface } from '../types/track.js';
import { cellCoordKey } from './circuitEdit.js';
import {
  canPlaceAmbientCell,
  isGrandstandCell,
  isInfrastructureCell,
  isSignCell,
  isWithinGrid,
  type GridCoord,
} from './circuitOccupancy.js';
function readCellFill(circuit: Circuit, x: number, y: number): CircuitSurface | undefined {
  return circuit.cellStyles?.[cellCoordKey(x, y)]?.fill;
}

function stripDecorAtKey(circuit: Circuit, x: number, y: number): Circuit {
  const key = cellCoordKey(x, y);
  const decorCells = (circuit.decorCells ?? []).filter(
    (cell) => cellCoordKey(cell.x, cell.y) !== key
  );
  return {
    ...circuit,
    decorCells: decorCells.length > 0 ? decorCells : undefined,
  };
}

export function clearAmbientFillAt(circuit: Circuit, x: number, y: number): Circuit {
  const key = cellCoordKey(x, y);
  const styles = { ...(circuit.cellStyles ?? {}) };
  const prev = styles[key];
  if (!prev?.fill) return circuit;
  const nextStyle = { ...prev, edges: prev.edges ? { ...prev.edges } : undefined };
  delete nextStyle.fill;
  if (!nextStyle.fill && (!nextStyle.edges || Object.keys(nextStyle.edges).length === 0)) {
    delete styles[key];
  } else {
    styles[key] = nextStyle;
  }
  return {
    ...circuit,
    cellStyles: Object.keys(styles).length > 0 ? styles : undefined,
  };
}

export function canPaintDecorSurface(circuit: Circuit, x: number, y: number): boolean {
  if (!isWithinGrid(circuit, x, y)) return false;
  if (isInfrastructureCell(circuit, x, y)) return false;
  if (isGrandstandCell(circuit, x, y) || isSignCell(circuit, x, y)) return false;
  return canPlaceAmbientCell(circuit, x, y);
}

export function getAmbientCellFill(
  circuit: Circuit,
  x: number,
  y: number
): CircuitSurface | undefined {
  if (isInfrastructureCell(circuit, x, y)) return undefined;
  return readCellFill(circuit, x, y);
}

export function isDecorSurfaceCell(circuit: Circuit, x: number, y: number): boolean {
  return getAmbientCellFill(circuit, x, y) !== undefined;
}

export function getDecorSurfacePlaceableCells(circuit: Circuit): GridCoord[] {
  const keys = new Set<string>();
  const result: GridCoord[] = [];
  for (let y = 0; y < circuit.height; y++) {
    for (let x = 0; x < circuit.width; x++) {
      if (!canPaintDecorSurface(circuit, x, y)) continue;
      const key = cellCoordKey(x, y);
      if (keys.has(key)) continue;
      keys.add(key);
      result.push({ x, y });
    }
  }
  return result;
}

export function addDecorSurfaceCell(
  circuit: Circuit,
  x: number,
  y: number,
  surface: CircuitSurface
): { circuit: Circuit } | { error: string } {
  if (!canPaintDecorSurface(circuit, x, y)) {
    return { error: 'Case invalide pour la surface décor' };
  }

  const key = cellCoordKey(x, y);
  const withoutDecor = stripDecorAtKey(circuit, x, y);
  const styles = { ...(withoutDecor.cellStyles ?? {}) };
  const prev = styles[key] ?? {};
  styles[key] = {
    ...prev,
    fill: surface,
    edges: prev.edges ? { ...prev.edges } : undefined,
  };

  return {
    circuit: {
      ...withoutDecor,
      cellStyles: styles,
    },
  };
}

export function removeDecorSurfaceAt(circuit: Circuit, x: number, y: number): Circuit {
  if (!isDecorSurfaceCell(circuit, x, y)) return circuit;
  return clearAmbientFillAt(circuit, x, y);
}

/** Clés cellStyles valides (tracé + surfaces décor hors piste). */
export function listValidCellStyleKeys(circuit: Circuit): Set<string> {
  const keys = new Set<string>();
  for (let y = 0; y < circuit.height; y++) {
    for (let x = 0; x < circuit.width; x++) {
      if (isInfrastructureCell(circuit, x, y)) {
        keys.add(cellCoordKey(x, y));
        continue;
      }
      const fill = readCellFill(circuit, x, y);
      const edges = circuit.cellStyles?.[cellCoordKey(x, y)]?.edges;
      if (fill && canPaintDecorSurface(circuit, x, y)) {
        keys.add(cellCoordKey(x, y));
      } else if (edges && Object.keys(edges).length > 0) {
        keys.add(cellCoordKey(x, y));
      }
    }
  }
  return keys;
}

export function pruneDecorSurfaceStyles(circuit: Circuit): Circuit {
  const styles = circuit.cellStyles;
  if (!styles) return circuit;
  const valid = listValidCellStyleKeys(circuit);
  const next: NonNullable<Circuit['cellStyles']> = {};
  for (const [key, style] of Object.entries(styles)) {
    if (!valid.has(key)) continue;
    const fill = style.fill;
    const edges = style.edges;
    if (!fill && !edges) continue;
    next[key] = {
      ...(fill ? { fill } : {}),
      ...(edges && Object.keys(edges).length > 0 ? { edges } : {}),
    };
  }
  return {
    ...circuit,
    cellStyles: Object.keys(next).length > 0 ? next : undefined,
  };
}
